import { Badge } from '../../components/Badge';
import { Checkbox } from '../../components/Checkbox';
import { MobileListItemConfig } from '../../components/data-display/ResponsiveDataView';
import { Machine } from '../../types/machines/machine.types';
import { getMachineFamilyImage } from './machineFamilyImage';
import { formatStatus, getMachineStatusVariant } from './machineColumnConfig';

interface MachineListItemMobileProps {
  machine: Machine;
  config: MobileListItemConfig<Machine>;
}

export function MachineListItemMobile({
  machine,
  config,
}: MachineListItemMobileProps) {
  const { isSelected, selectionMode, onSelect, onClick } = config;

  // EDMS's `state`, verbatim - same as the table. The synthetic-offline
  // disclosure lives in the machine side panel, not on this row.
  const statusVariant = getMachineStatusVariant(machine.status);

  return (
    <div
      className={`flex max-w-full items-center gap-4 p-4
        ${selectionMode ? 'active:bg-gray-100' : 'hover:bg-gray-50 active:bg-gray-100'}
        cursor-pointer transition-colors`}
      onClick={() => onClick?.(machine)}
    >
      {selectionMode && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect?.(machine.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${machine.displayName || machine.machineId}`}
          className='h-5 w-5 shrink-0'
        />
      )}

      <div className='h-14 w-14 shrink-0 overflow-hidden rounded bg-white'>
        <img
          src={getMachineFamilyImage(machine.family)}
          // The row shows name, status, customer and plant but not family, so
          // the picture is the only place that information appears.
          alt={machine.family || ''}
          className='h-full w-full object-contain'
          loading='lazy'
          decoding='async'
        />
      </div>

      <div className='min-w-0 flex-1 overflow-hidden'>
        <div className='mb-1 flex items-center gap-2'>
          <h3
            className='text-focus min-w-0 flex-1 truncate text-base
              font-semibold'
          >
            {machine.displayName || machine.machineId || 'Unknown'}
          </h3>
          <Badge variant={statusVariant} className='shrink-0'>
            {formatStatus(machine.status)}
          </Badge>
        </div>

        <p className='truncate text-sm text-gray-600'>
          {machine.customerName || 'Unknown'}
          {machine.plant && (
            <>
              {' • '}
              {machine.plant}
            </>
          )}
        </p>
      </div>
    </div>
  );
}
